import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";

import { CircularProgress, Dialog, FormControl, FormHelperText, IconButton, MenuItem, Select, TextField, Menu, } from "@mui/material";
import { Clock3, Plus, Trash2, ChevronRight, Bell, AlertTriangle, Layers, Mail, } from "lucide-react";

import { createTenantSlaAction, updateTenantSlaAction, } from "../../Actions/TenantSlaActions";
import { MyCloseIcon } from "../../Assets/svg/MyIcons";
import "../../components/Common/FormModal.css";
import "./AddEditTenantSla.css";

const PRIORITY_OPTIONS = [
    { value: "low", label: "Low", color: "#22c55e" },
    { value: "medium", label: "Medium", color: "#3b82f6" },
    { value: "high", label: "High", color: "#f59e0b" },
    { value: "critical", label: "Critical", color: "#ef4444" },
];

const AddEditTenantSla = ({
    open,
    handleClose,
    editData,
    setSnackbar,
    refresh,
}) => {
    const [loading, setLoading] = useState(false);
    const [stageMenuAnchor, setStageMenuAnchor] = useState(null);

    const isEdit = Boolean(editData);

    const formik = useFormik({
        initialValues: {
            slaName: editData?.slaName || "",
            description: editData?.description || "",
            priority: editData?.priority || "medium",
            stages: editData?.stages || [],
        },
        enableReinitialize: true,
        validationSchema: yup.object({
            slaName: yup
                .string()
                .trim()
                .required("SLA name is required")
                .max(100, "SLA name cannot exceed 100 characters"),
            description: yup.string().trim().nullable(),
            priority: yup.string().required("Priority is required"),
            stages: yup.array().min(1, "At least one SLA stage is required"),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                let response;
                const payload = {
                    slaName: values.slaName.trim(),
                    description: values.description?.trim() || null,
                    priority: values.priority,
                    stages: values.stages,
                };

                if (isEdit) {
                    response = await updateTenantSlaAction(editData.id, payload);
                } else {
                    response = await createTenantSlaAction(payload);
                }

                setSnackbar({
                    open: true,
                    message:
                        response?.data?.message || response?.message || "Success",
                    severity: "success",
                });
                handleClose();
                refresh();
            } catch (error) {
                setSnackbar({
                    open: true,
                    message:
                        error?.response?.data?.message || "Something went wrong",
                    severity: "error",
                });
            }
            setLoading(false);
        },
    });

    const SLA_STAGE_OPTIONS = [
        { value: 0, label: "Pending" },
        { value: 1, label: "Approved" },
        { value: 2, label: "Failed" },
        { value: 3, label: "Suspended" },
    ];

    const handleOpenStageMenu = (event) => {
        setStageMenuAnchor(event.currentTarget);
    };

    const handleCloseStageMenu = () => {
        setStageMenuAnchor(null);
    };

    const handleAddStage = (stageValue) => {
        if (stageValue === null || stageValue === undefined) return;

        const alreadyExists = formik.values.stages.some(
            (stage) => Number(stage.stage) === Number(stageValue)
        );
        if (alreadyExists) {
            handleCloseStageMenu();
            return;
        }

        formik.setFieldValue("stages", [
            ...formik.values.stages,
            {
                stageId: `stage_${Date.now()}`,
                stage: stageValue,
                time: "",
                unit: "hours",
                actions: ["notify"],
            },
        ]);
        handleCloseStageMenu();
    };

    const handleRemoveStage = (index) => {
        formik.setFieldValue(
            "stages",
            formik.values.stages.filter((_, i) => i !== index)
        );
    };

    const handleStageChange = (index, field, value) => {
        const updated = [...formik.values.stages];
        updated[index] = {
            ...updated[index],
            [field]: field === "stage" ? Number(value) : value,
        };
        formik.setFieldValue("stages", updated);
    };

    const handleActionChange = (stageIndex, actionIndex, value) => {
        const updatedStages = [...formik.values.stages];

        const currentActions = [
            ...(updatedStages[stageIndex].actions || ["notify"]),
        ];

        currentActions[actionIndex] = value;

        updatedStages[stageIndex] = {
            ...updatedStages[stageIndex],
            actions: currentActions,
        };

        formik.setFieldValue("stages", updatedStages);
    };

    const handleAddAction = (stageIndex) => {
        const updatedStages = [...formik.values.stages];

        const stage = updatedStages[stageIndex];

        const currentActions =
            stage.actions ||
            (stage.action ? [stage.action] : ["notify"]);

        if (currentActions.length >= 2) {
            return;
        }

        const remainingAction = currentActions.includes("notify")
            ? "email"
            : "notify";

        updatedStages[stageIndex] = {
            ...stage,
            actions: [...currentActions, remainingAction],
        };

        formik.setFieldValue("stages", updatedStages);
    };

    const handleRemoveAction = (stageIndex, actionIndex) => {
        const updatedStages = [...formik.values.stages];

        const currentActions = [
            ...(updatedStages[stageIndex].actions || ["notify"]),
        ];

        const updatedActions = currentActions.filter(
            (_, index) => index !== actionIndex
        );

        updatedStages[stageIndex] = {
            ...updatedStages[stageIndex],
            actions: updatedActions,
        };

        formik.setFieldValue("stages", updatedStages);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ className: "fm-dialog sla-dialog" }}
        >
            {/* Header */}
            <div className="fm-header">
                <span className="fm-header-icon" aria-hidden>
                    <Clock3 size={20} />
                </span>
                <div className="fm-header-copy">
                    <h2>{isEdit ? "Update Tenant SLA" : "Add Tenant SLA"}</h2>
                    <p>
                        {isEdit
                            ? "Update onboarding timelines and breach actions for each tenant stage."
                            : "Configure onboarding timelines and breach actions for each tenant stage."}
                    </p>
                </div>
                <IconButton
                    aria-label="close"
                    className="fm-close"
                    onClick={handleClose}
                    size="small"
                >
                    <MyCloseIcon />
                </IconButton>
            </div>

            <form onSubmit={formik.handleSubmit} className="fm-form">
                <div className="fm-body">
                    <div className="sla-top-grid">
                        <section className="fm-section">
                            <div className="fm-section-head">
                                <h3>SLA Information</h3>
                                <p>Define the basic details and purpose of this onboarding SLA.</p>
                            </div>

                            <div className="fm-card">
                                <div className="fm-grid is-stack">
                                    <div className="fm-field">
                                        <label>
                                            SLA Name
                                            <span className="fm-req">*</span>
                                        </label>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="slaName"
                                            placeholder="e.g. Standard Tenant Onboarding SLA"
                                            value={formik.values.slaName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.slaName &&
                                                Boolean(formik.errors.slaName)
                                            }
                                            helperText={
                                                formik.touched.slaName && formik.errors.slaName
                                            }
                                        />
                                    </div>

                                    <div className="fm-field">
                                        <label>Description</label>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            multiline
                                            minRows={3}
                                            name="description"
                                            placeholder="Describe the purpose and scope of this onboarding SLA..."
                                            value={formik.values.description}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.description &&
                                                Boolean(formik.errors.description)
                                            }
                                            helperText={
                                                formik.touched.description &&
                                                formik.errors.description
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Right – priority pills */}
                        <section className="fm-section">
                            <div className="fm-section-head">
                                <h3>Priority</h3>
                                <p>How urgently this SLA should be monitored.</p>
                            </div>

                            <div className="fm-card sla-priority-card">
                                <div className="sla-priority-grid">
                                    {PRIORITY_OPTIONS.map((opt) => {
                                        const selected = formik.values.priority === opt.value;
                                        return (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                className={`sla-priority-btn${selected ? " is-selected" : ""
                                                    }`}
                                                onClick={() =>
                                                    formik.setFieldValue("priority", opt.value)
                                                }
                                            >
                                                <span
                                                    className="sla-priority-dot"
                                                    style={{ backgroundColor: opt.color }}
                                                />
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                {formik.touched.priority && formik.errors.priority && (
                                    <FormHelperText error className="sla-priority-error">
                                        {formik.errors.priority}
                                    </FormHelperText>
                                )}
                            </div>
                        </section>
                    </div>

                    {/* Stages */}
                    <section className="fm-section">
                        <div className="fm-section-head sla-section-head">
                            <div className="sla-section-title">
                                <div className="sla-title-row">
                                    <h3>SLA Stages</h3>
                                    {formik.values.stages.length > 0 && (
                                        <span className="sla-count-chip">
                                            {formik.values.stages.length}{" "}
                                            {formik.values.stages.length === 1
                                                ? "stage"
                                                : "stages"}
                                        </span>
                                    )}

                                    <button
                                        type="button"
                                        className="sla-add-stage-btn"
                                        onClick={handleOpenStageMenu}
                                    >
                                        <Plus size={18} strokeWidth={2.2} />
                                        <span>
                                            {formik.values.stages.length > 0
                                                ? "Add another stage"
                                                : "Add Stage"}
                                        </span>
                                    </button>
                                </div>
                                <p>
                                    Define the maximum time allowed in each onboarding stage and the actions
                                    triggered when the SLA is breached.
                                </p>
                            </div>


                            <Menu
                                anchorEl={stageMenuAnchor}
                                open={Boolean(stageMenuAnchor)}
                                onClose={handleCloseStageMenu}
                                PaperProps={{ className: "sla-stage-menu" }}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                            >
                                {SLA_STAGE_OPTIONS.map((option) => {
                                    const alreadyAdded = formik.values.stages.some(
                                        (s) => Number(s.stage) === Number(option.value)
                                    );
                                    return (
                                        <MenuItem
                                            key={option.value}
                                            disabled={alreadyAdded}
                                            onClick={() => handleAddStage(option.value)}
                                        >
                                            <span>{option.label}</span>
                                            {alreadyAdded && (
                                                <span className="sla-stage-menu-added">Added</span>
                                            )}
                                        </MenuItem>
                                    );
                                })}
                            </Menu>
                        </div>

                        {formik.values.stages.length === 0 ? (
                            <div className="fm-card sla-empty-state">
                                <div className="sla-empty-icon">
                                    <Layers size={26} strokeWidth={1.5} />
                                </div>
                                <h4>No stages configured</h4>
                                <p>
                                    Add the first stage to define how long a tenant can remain
                                    in each onboarding step.
                                </p>
                                <button
                                    type="button"
                                    className="sla-empty-add-btn"
                                    onClick={handleOpenStageMenu}
                                >
                                    <Plus size={16} strokeWidth={2.2} />
                                    Add First Stage
                                </button>
                            </div>
                        ) : (
                            <div className="sla-stage-list">
                                {formik.values.stages.map((stage, index) => {
                                    const stageActions =
                                        stage.actions ||
                                        (stage.action ? [stage.action] : ["notify"]);

                                    return (
                                        <div
                                            key={stage.stageId || index}
                                            className="sla-stage-wrapper"
                                        >
                                            {index < formik.values.stages.length - 1 && (
                                                <div className="sla-stage-connector" />
                                            )}

                                            {/* sla-stage-card */}
                                            <div className="sla-stage-card">
                                                <div className="sla-stage-header">
                                                    <div className="sla-stage-heading">
                                                        <div className="sla-stage-number">
                                                            {String(index + 1).padStart(2, "0")}
                                                        </div>
                                                        <div>
                                                            <div className="sla-stage-title-row">
                                                                {/* <h4>Stage {index + 1}</h4>
                                                                <span
                                                                    className={`sla-status-chip sla-status-${String(
                                                                        stage.stage
                                                                    )}`}
                                                                >
                                                                    {SLA_STAGE_OPTIONS.find(
                                                                        (o) =>
                                                                            Number(o.value) === Number(stage.stage)
                                                                    )?.label || "Unknown"}
                                                                </span> */}
                                                                <h4>
                                                                    {SLA_STAGE_OPTIONS.find(
                                                                        (o) => Number(o.value) === Number(stage.stage)
                                                                    )?.label || "Unknown"}
                                                                </h4>

                                                                {/* <span className="sla-stage-sequence">
                                                                    Stage {index + 1}
                                                                </span> */}
                                                            </div>
                                                            <p>
                                                                Configure the maximum duration and breach actions for this stage.
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="sla-remove-btn"
                                                        onClick={() => handleRemoveStage(index)}
                                                        aria-label={`Remove stage ${index + 1}`}
                                                        title="Remove stage"
                                                    >
                                                        <Trash2 size={17} strokeWidth={1.9} />
                                                    </button>
                                                </div>

                                                <div className="sla-stage-body">
                                                    <div className="fm-field">
                                                        <label>Onboarding Stage</label>
                                                        <div className="sla-stage-value">
                                                            {
                                                                SLA_STAGE_OPTIONS.find(
                                                                    (o) =>
                                                                        Number(o.value) === Number(stage.stage)
                                                                )?.label
                                                            }
                                                        </div>
                                                    </div>

                                                    <div className="fm-field">
                                                        <label htmlFor={`sla-time-${index}`}>Maximum Duration</label>
                                                        <div className="sla-time-group">
                                                            <TextField
                                                                id={`sla-time-${index}`}
                                                                size="small"
                                                                type="number"
                                                                placeholder="e.g. 24"
                                                                value={stage.time || ""}
                                                                onChange={(e) =>
                                                                    handleStageChange(index, "time", e.target.value)
                                                                }
                                                                inputProps={{ min: 1 }}
                                                                className="sla-time-input"
                                                            />
                                                            <FormControl size="small" className="sla-unit-select">
                                                                <Select
                                                                    value={stage.unit || "hours"}
                                                                    onChange={(e) =>
                                                                        handleStageChange(index, "unit", e.target.value)
                                                                    }
                                                                    displayEmpty
                                                                >
                                                                    <MenuItem value="minutes">Minutes</MenuItem>
                                                                    <MenuItem value="hours">Hours</MenuItem>
                                                                    <MenuItem value="days">Days</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                        </div>
                                                        <span className="sla-field-hint">
                                                            Maximum time a tenant can remain in this stage before the SLA is breached.
                                                        </span>
                                                    </div>

                                                    {/* <div className="fm-field">
                                                        <label htmlFor={`sla-action-${index}`}>
                                                            Action on breach
                                                        </label>
                                                        <FormControl fullWidth size="small">
                                                            <Select
                                                                id={`sla-action-${index}`}
                                                                value={stage.action || "notify"}
                                                                onChange={(e) =>
                                                                    handleStageChange(
                                                                        index,
                                                                        "action",
                                                                        e.target.value
                                                                    )
                                                                }
                                                            >
                                                                <MenuItem value="notify">
                                                                    <div className="sla-action-option">
                                                                        <span className="sla-action-icon notify">
                                                                            <Bell size={15} />
                                                                        </span>
                                                                        Notify
                                                                    </div>
                                                                </MenuItem>
                                                                <MenuItem value="escalate">
                                                                    <div className="sla-action-option">
                                                                        <span className="sla-action-icon escalate">
                                                                            <AlertTriangle size={15} />
                                                                        </span>
                                                                        Escalate
                                                                    </div>
                                                                </MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                        <span className="sla-field-hint">
                                                            What happens when the time limit is reached.
                                                        </span>
                                                    </div> */}
                                                    <div className="fm-field sla-actions-field">

                                                        <div className="sla-action-header">
                                                            <label>
                                                                Breach Actions
                                                            </label>

                                                            <button
                                                                type="button"
                                                                className="sla-add-action-btn"
                                                                onClick={() => handleAddAction(index)}
                                                                disabled={
                                                                    stageActions.length >= 2
                                                                }
                                                            >
                                                                <Plus size={14} />
                                                                Add Action
                                                            </button>
                                                        </div>

                                                        <div className="sla-actions-list">

                                                            {stageActions.map(
                                                                (action, actionIndex) => (
                                                                    <div
                                                                        className="sla-action-row"
                                                                        key={`${stage.stageId}-action-${actionIndex}`}
                                                                    >

                                                                        <FormControl
                                                                            fullWidth
                                                                            size="small"
                                                                        >
                                                                            <Select
                                                                                id={`sla-action-${index}-${actionIndex}`}
                                                                                value={action}
                                                                                onChange={(e) =>
                                                                                    handleActionChange(
                                                                                        index,
                                                                                        actionIndex,
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                            >

                                                                                <MenuItem
                                                                                    value="notify"
                                                                                    disabled={
                                                                                        (stage.actions || [])
                                                                                            .includes("notify") &&
                                                                                        action !== "notify"
                                                                                    }
                                                                                >
                                                                                    <div className="sla-action-option">
                                                                                        <span className="sla-action-icon notify">
                                                                                            <Bell size={15} />
                                                                                        </span>

                                                                                        Notify
                                                                                    </div>
                                                                                </MenuItem>

                                                                                <MenuItem
                                                                                    value="email"
                                                                                    disabled={
                                                                                        (stage.actions || [])
                                                                                            .includes("email") &&
                                                                                        action !== "email"
                                                                                    }
                                                                                >
                                                                                    <div className="sla-action-option">
                                                                                        <span className="sla-action-icon email">
                                                                                            <Mail size={15} />
                                                                                        </span>

                                                                                        Email
                                                                                    </div>
                                                                                </MenuItem>

                                                                            </Select>
                                                                        </FormControl>

                                                                        {(stage.actions || []).length > 1 && (
                                                                            <button
                                                                                type="button"
                                                                                className="sla-remove-action-btn"
                                                                                onClick={() =>
                                                                                    handleRemoveAction(
                                                                                        index,
                                                                                        actionIndex
                                                                                    )
                                                                                }
                                                                                title="Remove action"
                                                                            >
                                                                                <Trash2 size={15} />
                                                                            </button>
                                                                        )}

                                                                    </div>
                                                                )
                                                            )}

                                                        </div>

                                                        <span className="sla-field-hint">
                                                            Actions triggered automatically when the SLA is breached.
                                                        </span>

                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        {formik.touched.stages && formik.errors.stages && (
                            <FormHelperText error className="sla-stages-error">
                                {formik.errors.stages}
                            </FormHelperText>
                        )}
                    </section>
                </div>

                <div className="fm-footer">
                    <button
                        type="button"
                        className="fm-btn"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="fm-btn is-primary"
                        disabled={loading}
                    >
                        {loading ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : isEdit ? (
                            "Update"
                        ) : (
                            "Add"
                        )}
                    </button>
                </div>
            </form>
        </Dialog>
    );
};

export default AddEditTenantSla;